import { useState } from "react";
import { Link } from "react-router-dom";
import { descargar, subirArchivo } from "../lib/api";
import { Aviso, Cabecera, Cargando, EstadoSocio, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";

interface Informe {
  simulado: boolean;
  total: number;
  validas: number;
  errores: { fila: number; mensaje: string }[];
  insertadas: number;
  muestra: {
    numeroSocio: number;
    nombre: string;
    apellidos: string;
    documentoNumero: string;
    estado: string;
    direccion: string;
  }[];
  nombrePartido: boolean;
  columnasJuntadas: boolean;
  direccionJuntada: boolean;
  numeroEnLaCalle: boolean;
  columnasReconocidas: string[];
}

/**
 * Importación de socios desde un CSV (migracion desde otro programa).
 * Primero se analiza el fichero sin guardar nada; solo si no hay errores se importa.
 */
export default function SociosImportar() {
  const { t, cuenta } = useIdioma();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [informe, setInforme] = useState<Informe | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");

  async function analizar() {
    if (!archivo) return;
    setError("");
    setTrabajando(true);
    try {
      setInforme(await subirArchivo<Informe>("/socios/importar?simular=1", archivo));
    } catch (fallo) {
      setInforme(null);
      setError(fallo instanceof Error ? fallo.message : t("si.falloLeer"));
    } finally {
      setTrabajando(false);
    }
  }

  async function importar() {
    if (!archivo) return;
    setError("");
    setTrabajando(true);
    try {
      setInforme(await subirArchivo<Informe>("/socios/importar", archivo));
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("si.falloImportar"));
    } finally {
      setTrabajando(false);
    }
  }

  const importado = informe && !informe.simulado && informe.insertadas > 0;
  // Si el nombre no venia en su columna, la previsualizacion lo ensena separado
  // del apellido: pegados en la misma celda daba igual donde hubiera caido el
  // corte, y no se veia si el apellido habia quedado entero.
  const aDosColumnas = Boolean(informe && (informe.nombrePartido || informe.columnasJuntadas));
  const conDireccion = Boolean(informe && informe.direccionJuntada);

  return (
    <>
      <Cabecera titulo={t("si.titulo")} descripcion={t("si.descripcion")}>
        <Link className="boton secundario" to="/socios">
          {t("si.volver")}
        </Link>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      {importado ? (
        <Panel>
          <Aviso tipo="ok">{cuenta(informe.insertadas, "si.importadoUno", "si.importados")}</Aviso>
          <div className="fila">
            <Link className="boton" to="/socios">
              {t("si.verSocios")}
            </Link>
            <button
              className="boton secundario"
              onClick={() => {
                setArchivo(null);
                setInforme(null);
              }}
            >
              {t("si.otroFichero")}
            </button>
          </div>
        </Panel>
      ) : (
        <div className="rejilla dos" style={{ alignItems: "start" }}>
          <div className="columna" style={{ gap: 16 }}>
            <Panel titulo={t("si.paso1")}>
              {/* Los nombres de las columnas son las cabeceras que el fichero
                  tiene que llevar, no texto de la pantalla; el importador las
                  reconoce en castellano y en catalan (ver ALIAS en csvLeer). */}
              <p className="suave" style={{ marginTop: 0 }}>
                {t("si.explicacion")}
              </p>
              <p className="suave">
                {t("si.explicacionNombreEntero")} {t("si.explicacionColumnasDobles")}{" "}
                {t("si.explicacionVarios")} {t("si.explicacionDireccion")}
              </p>
              <p className="suave pequeno">{t("si.formatos")}</p>
              <button
                className="boton secundario"
                onClick={() =>
                  descargar("/socios/importar/plantilla.csv", "plantilla-socios.csv").catch((fallo) =>
                    setError(fallo instanceof Error ? fallo.message : t("si.falloPlantilla")),
                  )
                }
              >
                {t("si.descargarPlantilla")}
              </button>
            </Panel>

            <Panel titulo={t("si.paso2")}>
              <input
                type="file"
                accept=".csv,.txt,text/csv"
                onChange={(e) => {
                  setArchivo(e.target.files?.[0] ?? null);
                  setInforme(null);
                  setError("");
                }}
              />
              <p className="suave pequeno">{t("si.analizarNoGuarda")}</p>
              <button className="boton" disabled={!archivo || trabajando} onClick={analizar}>
                {trabajando && !informe ? t("si.analizando") : t("si.analizar")}
              </button>
            </Panel>
          </div>

          <Panel titulo={t("si.paso3")} sinCuerpo>
            {trabajando ? (
              <Cargando texto={t("si.procesando")} />
            ) : !informe ? (
              <Vacio>{t("si.eligeFichero")}</Vacio>
            ) : (
              <>
                <div className="panel-cuerpo">
                  <div className="rejilla tres">
                    <TarjetaDato etiqueta={t("si.filas")} valor={informe.total} />
                    <TarjetaDato etiqueta={t("si.validas")} valor={informe.validas} color="var(--verde)" />
                    <TarjetaDato
                      etiqueta={t("si.conErrores")}
                      valor={informe.errores.length}
                      color={informe.errores.length > 0 ? "var(--rojo)" : undefined}
                    />
                  </div>
                  <p className="suave pequeno" style={{ marginBottom: 0 }}>
                    {t("si.columnas", { columnas: informe.columnasReconocidas.join(", ") })}
                  </p>
                </div>

                {informe.errores.length > 0 ? (
                  <>
                    <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                      <Aviso tipo="error">{t("si.corrige")}</Aviso>
                    </div>
                    <div className="tabla-envoltorio" style={{ maxHeight: 360, overflowY: "auto" }}>
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>{t("si.fila")}</th>
                            <th>{t("si.problema")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {informe.errores.map((e) => (
                            <tr key={e.fila}>
                              <td className="tabular">{e.fila}</td>
                              <td className="pequeno">{e.mensaje}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <>
                    {informe.nombrePartido && (
                      <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                        <Aviso tipo="atencion">{t("si.nombrePartido")}</Aviso>
                      </div>
                    )}
                    {informe.columnasJuntadas && (
                      <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                        <Aviso tipo="info">{t("si.columnasJuntadas")}</Aviso>
                      </div>
                    )}
                    {informe.direccionJuntada && (
                      <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                        <Aviso tipo="info">{t("si.direccionJuntada")}</Aviso>
                      </div>
                    )}
                    {/* Este si es importante: el club puede creer que en esa
                        columna estaban sus numeros de socio de siempre. */}
                    {informe.numeroEnLaCalle && (
                      <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
                        <Aviso tipo="atencion">{t("si.numeroEnLaCalle")}</Aviso>
                      </div>
                    )}
                    <div
                      className="tabla-envoltorio"
                      style={aDosColumnas || conDireccion ? { maxHeight: 360, overflowY: "auto" } : undefined}
                    >
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>{t("si.numero")}</th>
                            {aDosColumnas ? (
                              <>
                                <th>{t("si.nombre")}</th>
                                <th>{t("si.apellidos")}</th>
                              </>
                            ) : (
                              <th>{t("si.socio")}</th>
                            )}
                            <th>{t("si.documento")}</th>
                            {informe.direccionJuntada && <th>{t("si.direccion")}</th>}
                            <th>{t("si.estado")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {informe.muestra.map((s) => (
                            <tr key={s.documentoNumero}>
                              <td className="tabular suave">{s.numeroSocio}</td>
                              {aDosColumnas ? (
                                <>
                                  <td>{s.nombre}</td>
                                  <td>{s.apellidos}</td>
                                </>
                              ) : (
                                <td>
                                  {s.nombre} {s.apellidos}
                                </td>
                              )}
                              <td className="pequeno tabular">{s.documentoNumero}</td>
                              {informe.direccionJuntada && <td className="pequeno">{s.direccion}</td>}
                              <td>
                                <EstadoSocio estado={s.estado} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="panel-cuerpo">
                      {informe.validas > informe.muestra.length && (
                        <p className="suave pequeno" style={{ marginTop: 0 }}>
                          {t("si.seMuestran", { cuantos: informe.muestra.length, total: informe.validas })}
                        </p>
                      )}
                      <button className="boton grande" disabled={trabajando} onClick={importar}>
                        {cuenta(informe.validas, "si.importarUno", "si.importarVarios")}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </Panel>
        </div>
      )}
    </>
  );
}
